import { type Component, createSignal, Show, createMemo, createEffect } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { useTranslation } from '@/i18n';
import apiService from '@/services/api';
import MovementDiagram from './MovementDiagram';

interface StopCodeEntryProps {
  onSuccess: () => void;
  onClose?: () => void;
}

interface DiagramData {
  optimalRoute: number[];
  teamRoute: number[];
  allTiles: number[];
  routeEfficiency: number | null;
  solveTimes: Array<{
    tile_number: number;
    minutes_from_start: number | null;
    attempts: number;
  }>;
  teamName: string;
  gameName: string;
  finalTime?: string;
}

/**
 * StopCodeEntry - Modal for entering the stop code
 *
 * Flow:
 * 1. All tiles are solved (green) → this modal appears
 * 2. Team calculates the code offline using paper card formula
 * 3. Team enters N-digit code (length based on template's stop_code_length)
 * 4. If correct → timer stops → shows congratulations with fireworks
 * 5. If incorrect → error message, can retry
 */
const StopCodeEntry: Component<StopCodeEntryProps> = (props) => {
  const { t } = useTranslation();

  // Code length from template (the code itself is never sent to the team; default 6)
  const codeLength = createMemo(() => gameStore.gameState.template?.stop_code_length || 6);

  // Initialize digits array based on code length
  const [digits, setDigits] = createSignal<string[]>(Array(codeLength()).fill(''));

  // Sync digits array when code length changes (e.g., template data loads)
  createEffect(() => {
    const length = codeLength();
    if (digits().length !== length) {
      setDigits(Array(length).fill(''));
    }
  });

  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [isSuccess, setIsSuccess] = createSignal(false);
  const [finalTime, setFinalTime] = createSignal<string | null>(null);
  const [showDiagram, setShowDiagram] = createSignal(false);
  const [diagramData, setDiagramData] = createSignal<DiagramData | null>(null);
  const [diagramLoading, setDiagramLoading] = createSignal(false);

  // Get team name for display
  const teamName = createMemo(() => gameStore.gameState.team?.name || '');

  // Load movement diagram data
  const loadDiagramData = async () => {
    setDiagramLoading(true);
    try {
      const data = await apiService.getMovementDiagram();
      if (data.success) {
        setDiagramData({
          optimalRoute: data.optimal_route,
          teamRoute: data.team_route,
          allTiles: data.all_tiles,
          routeEfficiency: data.route_efficiency,
          solveTimes: data.solve_times,
          teamName: data.team_name,
          gameName: data.game_name,
          finalTime: data.final_time || undefined,
        });
        setShowDiagram(true);
      }
    } catch (err) {
      console.error('Failed to load diagram data:', err);
    } finally {
      setDiagramLoading(false);
    }
  };

  // Check if all digits are filled
  const isCodeComplete = createMemo(() =>
    digits().every(d => d !== '' && /^\d$/.test(d))
  );

  // Get the full code string
  const getCode = () => digits().join('');

  // Handle digit input
  const handleDigitInput = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...digits()];
    newDigits[index] = value;
    setDigits(newDigits);
    setError(null);

    // Auto-focus next input
    if (value && index < codeLength() - 1) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      nextInput?.focus();
    }
  };

  // Handle backspace - move to previous input
  const handleKeyDown = (index: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits()[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      prevInput?.focus();
    }
    if (e.key === 'Enter' && isCodeComplete()) {
      handleSubmit();
    }
  };

  // Handle paste - fill all digits
  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData?.getData('text') || '';
    const length = codeLength();
    const digitsOnly = pastedData.replace(/\D/g, '').slice(0, length);

    if (digitsOnly.length > 0) {
      const newDigits = [...digits()];
      for (let i = 0; i < length; i++) {
        newDigits[i] = digitsOnly[i] || '';
      }
      setDigits(newDigits);

      // Focus last filled or first empty
      const lastIndex = Math.min(digitsOnly.length, length - 1);
      const input = document.getElementById(`digit-${lastIndex}`);
      input?.focus();
    }
  };

  // Submit code to backend
  const handleSubmit = async () => {
    if (!isCodeComplete() || isSubmitting()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const code = getCode();
      const result = await apiService.stopClock(code);

      if (result.success) {
        // Success! Timer stopped
        setFinalTime(result.final_time || null);
        setIsSuccess(true);

        // Refresh dashboard to get updated state
        await gameStore.loadDashboard();

        // Notify parent after delay for animation
        setTimeout(() => {
          props.onSuccess();
        }, 5000);
      } else {
        // Wrong code
        setError(t('stopCode.wrongCode'));
        // Clear inputs for retry
        setDigits(Array(codeLength()).fill(''));
        document.getElementById('digit-0')?.focus();
      }
    } catch (err) {
      setError(t('stopCode.connectionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear all digits
  const handleClear = () => {
    setDigits(Array(codeLength()).fill(''));
    setError(null);
    document.getElementById('digit-0')?.focus();
  };

  // Success screen with fireworks
  if (isSuccess()) {
    return (
      <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop with confetti effect */}
        <div class="absolute inset-0 bg-black/80 backdrop-blur-md" />

        {/* Floating particles animation */}
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              class="absolute w-3 h-3 rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                'animation-delay': `${Math.random() * 5}s`,
                'animation-duration': `${3 + Math.random() * 4}s`,
                background: ['#44ff15', '#22009e', '#5500ee', '#fbbf24', '#ec4899', '#00d4ff'][i % 6],
                opacity: 0.7,
              }}
            />
          ))}
        </div>

        {/* Fireworks burst effect */}
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              class="absolute animate-firework"
              style={{
                left: `${15 + (i % 4) * 25}%`,
                top: `${20 + Math.floor(i / 4) * 40}%`,
                'animation-delay': `${i * 0.3}s`,
              }}
            >
              {[...Array(12)].map((_, j) => (
                <div
                  class="absolute w-2 h-2 rounded-full animate-firework-particle"
                  style={{
                    background: ['#ff0', '#f0f', '#0ff', '#0f0', '#ff4500', '#fff'][j % 6],
                    transform: `rotate(${j * 30}deg) translateY(-40px)`,
                    'animation-delay': `${i * 0.3}s`,
                  }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Success modal */}
        <div class="relative w-full max-w-md animate-scale-in">
          <div class="bg-gradient-to-br from-slate-900 via-emerald-900/50 to-slate-900 rounded-3xl border border-emerald-500/30 shadow-2xl overflow-hidden">
            {/* Top gradient bar */}
            <div class="h-2 bg-gradient-to-r from-emerald-500 via-cyan-500 to-green-500" />

            {/* Header */}
            <div class="text-center px-6 pt-8 pb-4">
              <div class="text-7xl mb-4 animate-bounce-slow">
                🎉
              </div>
              <h2 class="text-4xl font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-green-400 bg-clip-text text-transparent mb-2">
                {t('stopCode.congratulations')}
              </h2>
              <p class="text-white/70 text-lg">
                {teamName()}, {t('stopCode.clockStopped')}
              </p>
            </div>

            {/* Final time display */}
            <Show when={finalTime()}>
              <div class="px-6 py-4">
                <div class="bg-black/30 rounded-2xl border border-emerald-500/30 p-6 text-center">
                  <div class="text-sm text-white/50 uppercase tracking-wider mb-2">
                    {t('stopCode.yourTime')}
                  </div>
                  <div class="text-5xl font-black text-emerald-400 font-mono tracking-wider">
                    {finalTime()}
                  </div>
                </div>
              </div>
            </Show>

            {/* Movement Diagram Button */}
            <div class="px-6 pb-4">
              <button
                onClick={loadDiagramData}
                disabled={diagramLoading()}
                class="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/20 transition-all flex items-center justify-center gap-2"
              >
                {diagramLoading() ? (
                  <>
                    <span class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <span>🗺️</span>
                    {t('movementDiagram.showDiagram')}
                  </>
                )}
              </button>
            </div>

            {/* Celebration message */}
            <div class="px-6 pb-4 text-center">
              <p class="text-white/60 text-sm">
                {t('stopCode.waitForResults')}
              </p>
            </div>

            {/* Celebration icon */}
            <div class="text-center pb-6">
              <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-600 shadow-lg shadow-emerald-500/30">
                <span class="text-3xl">🎉</span>
              </div>
            </div>
          </div>
        </div>

        {/* Movement Diagram Modal */}
        <Show when={showDiagram() && diagramData()}>
          <MovementDiagram
            optimalRoute={diagramData()!.optimalRoute}
            teamRoute={diagramData()!.teamRoute}
            allTiles={diagramData()!.allTiles}
            routeEfficiency={diagramData()!.routeEfficiency}
            solveTimes={diagramData()!.solveTimes}
            teamName={diagramData()!.teamName}
            gameName={diagramData()!.gameName}
            finalTime={diagramData()!.finalTime}
            onClose={() => setShowDiagram(false)}
          />
        </Show>

        {/* Custom animations */}
        <style>{`
          @keyframes scale-in {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
          }

          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-15px); }
          }

          @keyframes float-particle {
            0%, 100% {
              transform: translateY(0) rotate(0deg);
              opacity: 0.7;
            }
            50% {
              transform: translateY(-120px) rotate(180deg);
              opacity: 0.3;
            }
          }

          @keyframes firework {
            0% { opacity: 0; transform: scale(0.3); }
            50% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(1.5); }
          }

          @keyframes firework-particle {
            0% { opacity: 1; transform: rotate(inherit) translateY(0); }
            100% { opacity: 0; transform: rotate(inherit) translateY(-60px); }
          }

          .animate-scale-in {
            animation: scale-in 0.5s ease-out;
          }

          .animate-bounce-slow {
            animation: bounce-slow 2s ease-in-out infinite;
          }

          .animate-float-particle {
            animation: float-particle 5s ease-in-out infinite;
          }

          .animate-firework {
            animation: firework 1.5s ease-out infinite;
          }

          .animate-firework-particle {
            animation: firework-particle 1.5s ease-out infinite;
          }
        `}</style>
      </div>
    );
  }

  // Code entry form
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={props.onClose}
      />

      {/* Modal */}
      <div class="relative w-full max-w-md animate-slide-up">
        {/* Close button - outside overflow container */}
        <Show when={props.onClose}>
          <button
            onClick={(e) => { e.stopPropagation(); props.onClose?.(); }}
            class="absolute -top-2 -right-2 z-20 w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all"
            aria-label="Zamknij"
          >
            <span class="text-3xl font-bold leading-none">&times;</span>
          </button>
        </Show>

        <div class="bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900 rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Top gradient bar */}
          <div class="h-1.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-purple-500" />

          {/* Header */}
          <div class="text-center px-6 pt-8 pb-4">
            <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center">
              <span class="text-3xl">🔐</span>
            </div>
            <h2 class="text-2xl font-bold text-white mb-2">
              {t('stopCode.enterCode')}
            </h2>
            <p class="text-white/60 text-sm">
              {t('stopCode.allSolvedHint')}
            </p>
          </div>

          {/* Code input */}
          <div class="px-6 py-4">
            <div class="flex justify-center gap-2">
              {digits().map((digit, index) => (
                <input
                  id={`digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onInput={(e) => handleDigitInput(index, (e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  class={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all
                    bg-black/30 text-white focus:outline-none
                    ${error()
                      ? 'border-red-500/50 focus:border-red-500'
                      : 'border-white/20 focus:border-emerald-500'
                    }
                    ${digit ? 'bg-emerald-500/10 border-emerald-500/30' : ''}
                  `}
                  disabled={isSubmitting()}
                />
              ))}
            </div>

            {/* Error message */}
            <Show when={error()}>
              <div class="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                <p class="text-red-400 text-sm">{error()}</p>
              </div>
            </Show>
          </div>

          {/* Info hint */}
          <div class="px-6 pb-4">
            <div class="bg-white/5 rounded-xl p-3 border border-white/10">
              <p class="text-white/50 text-xs text-center">
                {t('stopCode.calculateHint', { digits: codeLength() })}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div class="px-6 pb-6 flex gap-3">
            <button
              onClick={handleClear}
              class="flex-1 py-3 rounded-xl font-medium text-white/70 bg-white/10 hover:bg-white/15 transition-colors"
              disabled={isSubmitting()}
            >
              {t('common.clear')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isCodeComplete() || isSubmitting()}
              class={`flex-1 py-3 rounded-xl font-bold transition-all
                ${isCodeComplete() && !isSubmitting()
                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 active:scale-[0.98]'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
                }
              `}
            >
              {isSubmitting() ? (
                <span class="flex items-center justify-center gap-2">
                  <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  {t('stopCode.checking')}
                </span>
              ) : (
                t('stopCode.stopClock')
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default StopCodeEntry;
